/**
 * ReportsSection - Turnover reports by period
 * Shows deposits, withdrawals, net profit, and user stats
 *
 * Uses shared components:
 * - StatsCard (core) - stat display cards
 * - SingleOptionFilter (composed) - period selector
 * - MenuButton (composed) - navigation menu
 */

import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  UserPlus,
  Users,
  LayoutDashboard,
  ArrowDownToLine,
  ArrowUpFromLine,
  FileText,
  Gift,
  Loader2,
} from 'lucide-react';
import { StatsCard, SingleOptionFilter, MenuButton } from '@/components/shared';
import type { MenuOption } from '@/components/shared/composed/MenuButton';
import type { TurnoverReportResponse } from '@/types';

export type ReportPeriod = 'weekly' | 'monthly' | 'yearly';

export interface ReportsSectionProps {
  /** Report data - matches TurnoverReportResponse */
  report: TurnoverReportResponse | null;
  /** Currently selected period */
  period: ReportPeriod;
  /** Loading state */
  isLoading?: boolean;
  /** Callback when period changes */
  onPeriodChange?: (period: ReportPeriod) => void;
  /** Callback when navigating to another page */
  onNavigate?: (page: string) => void;
}

// Period options for SingleOptionFilter
const periodOptions = [
  { value: 'weekly', label: 'Weekly (7 days)' },
  { value: 'monthly', label: 'Monthly (30 days)' },
  { value: 'yearly', label: 'Yearly (365 days)' },
];

// Admin menu options
const adminMenuOptions: MenuOption[] = [
  { value: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { value: 'users', label: 'Users', icon: Users },
  { value: 'deposits', label: 'Deposits', icon: ArrowDownToLine },
  { value: 'withdrawals', label: 'Withdrawals', icon: ArrowUpFromLine },
  { value: 'reports', label: 'Reports', icon: FileText },
  { value: 'bonus', label: 'Bonus', icon: Gift },
  { value: 'referees', label: 'Referees', icon: Users },
];

/**
 * Format currency for display
 */
function formatCurrency(amount: number): string {
  return `${amount.toLocaleString()} ₼`;
}

/**
 * Get period display label
 */
function getPeriodLabel(period: ReportPeriod): string {
  switch (period) {
    case 'weekly':
      return 'Last 7 days';
    case 'monthly':
      return 'Last 30 days';
    case 'yearly':
      return 'Last 365 days';
  }
}

export function ReportsSection({
  report,
  period,
  isLoading = false,
  onPeriodChange,
  onNavigate,
}: ReportsSectionProps) {
  return (
    <div className="space-y-6 pb-6">
      {/* Header with menu */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-bold text-slate-800">Reports</h2>
          {isLoading && (
            <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />
          )}
        </div>
        <MenuButton
          options={adminMenuOptions}
          value="reports"
          onSelect={(page) => onNavigate?.(page)}
          sheetTitle="Navigate"
        />
      </div>

      {/* Period selector - using SingleOptionFilter */}
      <section>
        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-4">
          Period
        </h3>
        <div className="flex items-center gap-3">
          <SingleOptionFilter
            options={periodOptions}
            value={period}
            onChange={(value) => onPeriodChange?.(value as ReportPeriod)}
            sheetTitle="Select Period"
            showAllOption={false}
          />
          <span className="text-sm text-slate-600">{getPeriodLabel(period)}</span>
        </div>
      </section>

      {/* Financial Stats */}
      <section className={isLoading ? 'opacity-60 transition-opacity' : ''}>
        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-4">
          {report?.period ?? 'Summary'} ({report?.days ?? 0} days)
        </h3>
        <div className="space-y-4">
          <StatsCard
            title="Deposits"
            value={formatCurrency(report?.deposits.amount ?? 0)}
            subtitle={`${report?.deposits.count ?? 0} transactions`}
            icon={TrendingUp}
            variant="success"
          />
          <StatsCard
            title="Withdrawals"
            value={formatCurrency(report?.withdrawals.amount ?? 0)}
            subtitle={`${report?.withdrawals.count ?? 0} transactions`}
            icon={TrendingDown}
            variant="error"
          />
          <StatsCard
            title="Net Profit"
            value={formatCurrency(report?.net ?? 0)}
            icon={DollarSign}
            variant={report?.net && report.net > 0 ? 'success' : 'default'}
          />
        </div>
      </section>

      {/* User Stats */}
      <section className={isLoading ? 'opacity-60 transition-opacity' : ''}>
        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-4">
          Users
        </h3>
        <div className="space-y-4">
          <StatsCard
            title="New Users"
            value={report?.newUsers ?? 0}
            subtitle="Registered this period"
            icon={UserPlus}
            variant="info"
          />
        </div>
      </section>
    </div>
  );
}
